import { useState, useCallback, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getAllImagesQueryOptions } from '@/api/queries/images';
import { downloadAndStoreImage } from '@/db/images';
import { CueingImagesResponse } from '@/api/schema/images';
import { chunk } from 'lodash-es';
export interface PrefetchProgress {
  totalSize: number;
  completedSize: number;
  failedSize: number;
  skippedSize: number;
  totalCount: number;
  completedCount: number;
  failedCount: number;
  skippedCount: number;
}

export interface PrefetchState {
  isLoading: boolean;
  isDownloading: boolean;
  progress: PrefetchProgress;
  error: string | null;
}

const initialProgress: PrefetchProgress = {
  totalSize: 0,
  completedSize: 0,
  failedSize: 0,
  skippedSize: 0,
  totalCount: 0,
  completedCount: 0,
  failedCount: 0,
  skippedCount: 0,
};

const initialState: PrefetchState = {
  isLoading: true,
  isDownloading: false,
  progress: initialProgress,
  error: null,
};

const getPartialProgress = (
  prev: PrefetchState,
  status: string,
  fileSize: number,
): Partial<PrefetchProgress> => {
  switch (status) {
    case 'success':
      return {
        completedSize: prev.progress.completedSize + fileSize,
        completedCount: prev.progress.completedCount + 1,
      };
    case 'skipped':
      return {
        skippedSize: prev.progress.skippedSize + fileSize,
        skippedCount: prev.progress.skippedCount + 1,
      };
    case 'error':
    default:
      return {
        failedSize: prev.progress.failedSize + fileSize,
        failedCount: prev.progress.failedCount + 1,
      };
  }
};

export const usePrefetchImages = () => {
  const [ state, setState ] = useState<PrefetchState>(initialState);

  const allImagesQuery = useQuery(getAllImagesQueryOptions);

  const prefetchImages = useCallback(
    async (imagesData?: CueingImagesResponse) => {
      if (!imagesData) {
        setState(prev => ({
          ...prev,
          error: 'No images data available',
        }));
        return;
      }

      setState(prev => ({
        ...prev,
        isLoading: false,
        isDownloading: true,
        error: null,
        progress: initialProgress,
      }));

      try {
        let totalSize = 0;
        let totalCount = 0;
        const imagesToProcess: {
          id: string;
          dir: string;
          file: {
            filename: string;
            size: number;
            lastModified: string;
            contentType: string;
            url: string;
          };
        }[] = [];

        for (const [ dir, files ] of Object.entries(imagesData.images)) {
          for (const file of files) {
            const id = `${dir}${file.filename}`;
            totalSize += file.size;
            totalCount++;
            imagesToProcess.push({
              id,
              dir,
              file: {
                filename: file.filename,
                size: file.size,
                lastModified: file.lastModified,
                contentType: file.contentType ?? 'image/jpeg',
                url: file.url,
              },
            });
          }
        }

        setState(prev => ({
          ...prev,
          progress: {
            ...prev.progress,
            totalSize,
            totalCount,
            pendingSize: totalSize,
            pendingCount: totalCount,
          },
        }));

        const chunks = chunk(imagesToProcess, 5); // Process 5 images at a time
        // Process each chunk sequentially to limit concurrency
        for (const imagesChunk of chunks) {
          await Promise.all(
            imagesChunk.map(async ({ id, dir, file }) => {
              try {
                const result = await downloadAndStoreImage(id, dir, file);

                setState(prev => ({
                  ...prev,
                  progress: {
                    ...prev.progress,
                    ...getPartialProgress(prev, result.status, file.size),
                  },
                }));
              }
              catch {
                setState(prev => ({
                  ...prev,
                  progress: {
                    ...prev.progress,
                    ...getPartialProgress(prev, 'error', file.size),
                  },
                }));
              }
            }),
          );
        }

        setState(prev => ({
          ...prev,
          isDownloading: false,
        }));
      }
      catch (error) {
        setState(prev => ({
          ...prev,
          isLoading: false,
          isDownloading: false,
          error:
            error instanceof Error
              ? error.message
              : 'Failed to prefetch images',
        }));
      }
    },
    [],
  );

  const reset = useCallback(() => {
    setState(initialState);
  }, []);

  useEffect(() => {
    if (allImagesQuery.isSuccess) {
      void prefetchImages(allImagesQuery.data);
    }
  }, [ allImagesQuery.isSuccess, allImagesQuery.data ]);

  return {
    ...state,
    isLoading: state.isLoading || allImagesQuery.isLoading,
    error:
      state.error
      ?? (allImagesQuery.error ? allImagesQuery.error.message : null),
    prefetchImages,
    reset,
    isActive:
      state.isLoading || state.isDownloading || allImagesQuery.isFetching,
    progressPercentage:
      state.progress.totalSize > 0
        ? Math.round(
          ((state.progress.completedSize
            + state.progress.failedSize
            + state.progress.skippedSize)
          / state.progress.totalSize)
        * 100,
        )
        : 0,
    progressCountPercentage:
      state.progress.totalCount > 0
        ? Math.round(
          ((state.progress.completedCount
            + state.progress.failedCount
            + state.progress.skippedCount)
          / state.progress.totalCount)
        * 100,
        )
        : 0,
  };
};
